import React, { useRef, useState } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { getConnectionItemLabelText, isV2PanelistLink } from '../../../../../infrastructure/utils/connections.utils';
import { Popover } from '../../../../global/popover/popover';
import Text from '../../../../global/text/text';
import { ConnectionType, IConnectionDetailsDTO, IDeleteConnectionRequestParams } from '../../../../../api/connections.dao';
import { ConnectionVersion, MEDIUM_PLAIN_TEXT_LENGTH } from '../../../../../constants/common.constants';
import { CopyButton } from '../../../../global/copy-button/copy-button';
import { getKnovioPresentationUrlById } from '../../../../../infrastructure/utils/knovio.utils';
import { ConnectionDetailsItem } from '../../shared/connections-list/_connection-details-item';
import { ConfirmDeletePopover, IDeleteConnectionDetails } from '../../shared/connections-list/_connection-delete-confirm-popover';

import './_control-form.scss';
import { WebinarReportsViewModel } from '../../shared/webinar-report/webinar-reports.viewmodel';
import { CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, OpenFrom } from '../../../../../tracking-service/tracking-values';
import { MeetingApiParams } from '../../shared/connections-list/_connection-form/connection-form.viewmodel';
import { Permission } from '../../../../../api/roles.dao';
import { hasPermission } from '../../../../../infrastructure/utils/has-permissions';
import KnovioPreviewLinkPopover from '../../shared/knovio-preview-link-popover/knovio-preview-link-popover';
import KnovioPreviewLinkPopoverVM, { KnovioPreviewSourceType } from '../../shared/knovio-preview-link-popover/knovio-preview-link-popover.viewmodel';

interface IProps {
    eventTimezone: string;
    meetingConnections: IConnectionDetailsDTO[];
    roomConnections?: IConnectionDetailsDTO[];
    onEditConnection: (connection: IConnectionDetailsDTO) => void;
    showConfigurePassportForm: (connection: IConnectionDetailsDTO) => void;
    onDeleteConnection: (data: IDeleteConnectionRequestParams, connectionDetails?: IDeleteConnectionDetails) => void;
    defaultRoomConnectionId?: number;
    defaultMeetingConnectionId?: number;
    cloneConnection: (connection: IConnectionDetailsDTO) => void;
    onMeetingConnectionStatusChange?: (meetingConnectionId: number) => void;
    onRoomConnectionStatusChange?: (meetingConnectionId: number) => void;
    webinarReportsVM?: WebinarReportsViewModel;
    handleTroubleshoot: (connection: IConnectionDetailsDTO) => void;
    trackHostJoinClick: (eventName: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, openFrom: OpenFrom) => void;
    meetingParams: MeetingApiParams;
    shouldDisableDeleteButton: (connection: IConnectionDetailsDTO) => boolean;
}

interface ICellProps {
    copyValue: string;
    className: string;
    eventTimezone?: string;
}
interface IRowProps {
    connection: IConnectionDetailsDTO;
    id: string;
    eventTimezone: string;
    connectionAccount: string;
    connectionName: string;
    connectionLink: string;
    cloneConnection?: (connection: IConnectionDetailsDTO) => void;
    onEditConnection: (connection: IConnectionDetailsDTO) => void;
    showConfigurePassportForm: (connection: IConnectionDetailsDTO) => void;
    setDeletePopoverConnection: (connection: IConnectionDetailsDTO | null) => void;
    isDefault: boolean;
    onConnectionStatusChange: (meetingConnectionId: number) => void;
    webinarReportsVM?: WebinarReportsViewModel;
    handleTroubleshoot: (connection: IConnectionDetailsDTO) => void;
    trackHostJoinClick: (eventName: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, openFrom: OpenFrom) => void;
    meetingParams: MeetingApiParams;
    shouldDisableDeleteButton: boolean;
}

interface IControlFormConnectionsDetailProps extends ICellProps {
    eventTimezone: string;
    connection?: IConnectionDetailsDTO;
    cloneConnection?: (connection: IConnectionDetailsDTO) => void;
    onEditConnection?: (connection: IConnectionDetailsDTO) => void;
    showConfigurePassportForm?: (connection: IConnectionDetailsDTO) => void;
    setDeletePopoverConnection?: (connection: IConnectionDetailsDTO | null) => void;
    isDefault?: boolean;
    onConnectionStatusChange?: (meetingConnectionId: number) => void;
    webinarReportsVM?;
    handleTroubleshoot?: (connection: IConnectionDetailsDTO) => void;
    trackHostJoinClick?: (eventName: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, openFrom: OpenFrom) => void;
    meetingParams?: MeetingApiParams;
    shouldDisableDeleteButton?: boolean;
}

const ControlFormConnectionsDetail = ({
    className,
    connection,
    cloneConnection,
    onEditConnection,
    showConfigurePassportForm,
    setDeletePopoverConnection,
    isDefault,
    eventTimezone,
    onConnectionStatusChange,
    webinarReportsVM,
    handleTroubleshoot,
    trackHostJoinClick,
    meetingParams,
    shouldDisableDeleteButton,
}: IControlFormConnectionsDetailProps) => {
    const knovioPreviewLinkPopoverInstance = new KnovioPreviewLinkPopoverVM(KnovioPreviewSourceType.Connection);

    const openKnovioPreviewLinkPopover = connectionDTO => {
        if (connectionDTO && connectionDTO.type !== ConnectionType.Knovio && hasPermission(Permission.PREVIEW_KNOVIO_CONNECTION)) return;

        knovioPreviewLinkPopoverInstance.setConnectionInfo({
            ...meetingParams,
            meetingConnectionDetailsId: connectionDTO.meetingConnectionId
        });

        knovioPreviewLinkPopoverInstance.openPopover();
    };
    const isV2PanelistConnection = isV2PanelistLink(connection);

    return (
        <div className={`${className} control-form-connections-item__hoverable`}>
            <ConnectionDetailsItem
                connection={connection}
                cloneConnection={cloneConnection}
                eventTimezone={eventTimezone}
                handleOnChange={onEditConnection}
                showConfigurePassportForm={showConfigurePassportForm}
                setDeletePopoverConnection={setDeletePopoverConnection}
                isDefault={isDefault}
                onConnectionStatusChange={onConnectionStatusChange}
                checkbox={!isV2PanelistConnection}
                controlledCheckbox={!isV2PanelistConnection}
                webinarReportsVM={webinarReportsVM}
                handleTroubleshoot={handleTroubleshoot}
                trackHostJoinClick={trackHostJoinClick}
                meetingParams={meetingParams}
                onOpenKnovioPreviewLinkPopover={() => openKnovioPreviewLinkPopover(connection)}
                shouldDisableDeleteButton={shouldDisableDeleteButton}
            />
             <KnovioPreviewLinkPopover vm={knovioPreviewLinkPopoverInstance}/>
        </div>
    );
};

const ControlFormConnectionsCell = ({ copyValue, className }: ICellProps) => (
    <>
        {copyValue ? (
            <div
                className={`${className} control-form-connections-item__hoverable`}
                onClick={() => navigator.clipboard.writeText(copyValue)}
            >
                <Text message={copyValue} withEllipsis />
                <CopyButton copyValue={copyValue} className='control-form-connections-item__copy' />
            </div>
        ) : (
            <div className={className}>
                <Text message={copyValue} withEllipsis />
            </div>
        )}
    </>
);

const ControlFormConnectionsNotes = ({ copyValue, className }: ICellProps) => {
    const [isModalOpen, setModalOpen] = useState(false);
    const buttonRef = useRef(null);

    const closePopover = (e?: React.ChangeEvent<HTMLInputElement>) => {
        if (e) e.stopPropagation();
        setModalOpen(false);
    };

    return (
        <>
            {copyValue ? (
                <div
                    ref={buttonRef}
                    className={classnames(`${className} control-form-connections-item__hoverable`, {
                        'status-button--active': isModalOpen
                    })}
                >
                    <Text message={copyValue} withEllipsis onClick={() => setModalOpen(prevState => !prevState)} />
                    <CopyButton copyValue={copyValue} className='control-form-connections-item__copy' />
                    <Popover
                        padding={0}
                        isOpen={isModalOpen}
                        buttonRef={buttonRef}
                        customWidth={buttonRef.current?.getBoundingClientRect().width * 2}
                        onClickOutside={closePopover}
                    >
                        <div className='status-button__content' onClick={e => e.stopPropagation()}>
                            <div className='status-button__content-label'>
                                <FormattedMessage
                                    id='oe.common.connectionNotes'
                                    defaultMessage='Connection Notes'
                                    description='Table header'
                                    values={{
                                        length: MEDIUM_PLAIN_TEXT_LENGTH
                                    }}
                                />
                            </div>
                            <div className='note-content'>{copyValue}</div>
                        </div>
                    </Popover>
                </div>
            ) : (
                <div />
            )}
        </>
    );
};

const ControlFormConnectionsRow = ({
    connection,
    id,
    cloneConnection,
    connectionAccount,
    connectionName,
    connectionLink,
    onEditConnection,
    showConfigurePassportForm,
    setDeletePopoverConnection,
    isDefault,
    eventTimezone,
    onConnectionStatusChange,
    webinarReportsVM,
    handleTroubleshoot,
    trackHostJoinClick,
    meetingParams,
    shouldDisableDeleteButton,
}: IRowProps) => (
    <div className='control-form-connections-item' key={id}>
        {[
            {
                copyValue: connectionName,
                className: isV2PanelistLink(connection) ? 'control-form-connections-item__child-connection' : 'control-form-connections-item__connection',
                connection: connection,
                onEditConnection: onEditConnection,
                showConfigurePassportForm: showConfigurePassportForm,
                setDeletePopoverConnection: setDeletePopoverConnection,
                isDefault: isDefault,
                eventTimezone: eventTimezone,
                cloneConnection: cloneConnection,
                onConnectionStatusChange: onConnectionStatusChange,
                webinarReportsVM: webinarReportsVM,
                component: ControlFormConnectionsDetail,
                handleTroubleshoot: handleTroubleshoot,
                trackHostJoinClick: trackHostJoinClick,
                meetingParams: meetingParams,
                shouldDisableDeleteButton: shouldDisableDeleteButton,
            },
            { copyValue: connectionLink, className: 'control-form-connections-item__link' },
            { copyValue: id, className: 'control-form-connections-item__id' },
            { copyValue: connection.connectionPassword, className: 'control-form-connections-item__password' },
            { copyValue: connectionAccount, className: 'control-form-connections-item__account' },
            { copyValue: connection.sip, className: 'control-form-connections-item__sip' },
            {
                copyValue: connection.connectionNotes,
                className: 'control-form-connections-item__connection-notes',
                component: ControlFormConnectionsNotes
            },
            { copyValue: connection.dialForVs, className: 'control-form-connections-item__dial' }
        ].map(({ component: CellComponent = ControlFormConnectionsCell, ...c }) => (
            <CellComponent {...c} key={connection.meetingConnectionId + c.className} eventTimezone={eventTimezone} />
        ))}
    </div>
);

const ControlFormConnections = ({
    meetingConnections,
    eventTimezone,
    roomConnections = [],
    onEditConnection,
    showConfigurePassportForm,
    onDeleteConnection,
    cloneConnection,
    defaultRoomConnectionId,
    defaultMeetingConnectionId,
    onMeetingConnectionStatusChange,
    onRoomConnectionStatusChange,
    webinarReportsVM,
    handleTroubleshoot,
    trackHostJoinClick,
    meetingParams,
    shouldDisableDeleteButton,
}: IProps) => {
    const [deletePopoverConnection, setDeletePopoverConnection] = useState<IConnectionDetailsDTO | null>(null);
    const renderConnection = (connection, isDefault, onConnectionStatusChange) => {
        const isKnovio = connection.type === ConnectionType.Knovio;
        const isVeracast = connection.type === ConnectionType.Veracast;
        const id = isKnovio ? connection.embedCodeId : isVeracast ? connection.veracastEventID : connection.connectionId || '';
        const connectionAccount = isKnovio ? connection.accountName : connection.connectionAccount;
        const connectionLink = isKnovio
            ? getKnovioPresentationUrlById(connection.embedCodeId)
            : connection.connectionLink;
        const connectionName = getConnectionItemLabelText(connection);

        return (
            <ControlFormConnectionsRow
                connection={connection}
                eventTimezone={eventTimezone}
                key={connection.meetingConnectionId}
                id={id}
                cloneConnection={cloneConnection}
                connectionAccount={connectionAccount}
                connectionName={connectionName}
                connectionLink={connectionLink}
                onEditConnection={onEditConnection}
                showConfigurePassportForm={showConfigurePassportForm}
                setDeletePopoverConnection={setDeletePopoverConnection}
                isDefault={isDefault}
                onConnectionStatusChange={onConnectionStatusChange}
                webinarReportsVM={webinarReportsVM}
                handleTroubleshoot={handleTroubleshoot}
                trackHostJoinClick={trackHostJoinClick}
                meetingParams={meetingParams}
                shouldDisableDeleteButton={shouldDisableDeleteButton(connection)}
            />
        );
    };
    return (
        <div className='control-form-connections'>
            {roomConnections.map(connection => renderConnection(
                connection, defaultRoomConnectionId === connection.meetingConnectionId, onRoomConnectionStatusChange))}
            {meetingConnections.length > 0 && (
                <>
                    <span className='control-form__room'>
                        <label className='oe-form-field__title'>Meeting Connections</label>
                    </span>
                    {meetingConnections.map(connection => (
                        <div key={connection.meetingConnectionId}>
                            {renderConnection(
                                connection, defaultMeetingConnectionId === connection.meetingConnectionId, onMeetingConnectionStatusChange)}
                            {connection.childConnections && connection.childConnections.length > 0 && (
                                <div key={connection.meetingConnectionId}>
                                    {connection.childConnections.map(childConnection => (
                                        <div className='control-form-connections__child-connections' key={childConnection.meetingConnectionId}>
                                            {renderConnection(
                                                childConnection,
                                                defaultMeetingConnectionId === childConnection.meetingConnectionId,
                                                onMeetingConnectionStatusChange
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </>
            )}
            <ConfirmDeletePopover
                handleOnDelete={onDeleteConnection}
                deletePopoverConnection={deletePopoverConnection}
                setDeletePopoverConnection={setDeletePopoverConnection}
            />
        </div>
    );
};

const ControlFormListOfConnections = ({
    connections,
    defaultConnectionId,
    onChange,
    showConfigurePassportForm,
    onDelete,
    cloneConnection,
    onConnectionStatusChange,
    controlledCheckbox,
    checkbox,
    eventTimezone,
    webinarReportsVM,
    handleTrackingEvent,
    handleTroubleshoot,
    trackHostJoinClick,
    meetingParams,
    hideChevron,
    shouldDisableDeleteButton,
    connectionVersion,
    openFrom
}: {
    eventTimezone: string;
    connections: IConnectionDetailsDTO[];
    defaultConnectionId: number | null | undefined;
    onChange: (connection: IConnectionDetailsDTO) => void;
    showConfigurePassportForm: (connection: IConnectionDetailsDTO) => void;
    onDelete: (data: IDeleteConnectionRequestParams, connectionDetails?: IDeleteConnectionDetails) => void;
    cloneConnection: (connection: IConnectionDetailsDTO) => void;
    onConnectionStatusChange?: (meetingConnectionId: number) => void;
    controlledCheckbox?: boolean;
    checkbox: boolean;
    webinarReportsVM?: WebinarReportsViewModel;
    handleTrackingEvent?: (event: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS) => void;
    handleTroubleshoot: (connection: IConnectionDetailsDTO) => void;
    trackHostJoinClick: (eventName: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, openFrom: OpenFrom) => void;
    meetingParams: MeetingApiParams;
    hideChevron?: boolean;
    shouldDisableDeleteButton: (connection: IConnectionDetailsDTO) => boolean;
    connectionVersion?: ConnectionVersion;
    openFrom?: OpenFrom;
}) => {
    const [deletePopoverConnection, setDeletePopoverConnection] = useState<IConnectionDetailsDTO | null>(null);
    const knovioPreviewLinkPopoverInstance = new KnovioPreviewLinkPopoverVM(KnovioPreviewSourceType.Connection);

    const openKnovioPreviewLinkPopover = connection => {
        if (connection?.type === ConnectionType.Knovio && hasPermission(Permission.PREVIEW_KNOVIO_CONNECTION)) {
            knovioPreviewLinkPopoverInstance.setConnectionInfo({
                ...meetingParams,
                meetingConnectionDetailsId: connection.meetingConnectionId
            });

            knovioPreviewLinkPopoverInstance.openPopover();
        };
    };

    const renderChildConnections = (childConnections: IConnectionDetailsDTO[]) => {
        return childConnections.map(childConnection => (
            <div key={childConnection.joinMethodId}
                className={classnames('connection-list__connection-wrapper', {
                    'connection-list__child-connections': openFrom === OpenFrom.RunMeeting && connectionVersion === ConnectionVersion.V2,
                    'connection-list__child-connection-wrapper': connectionVersion === ConnectionVersion.V2 && openFrom === OpenFrom.Control
                }
            )}>
                <ConnectionDetailsItem
                    key={childConnection.joinMethodId}
                    connection={childConnection}
                    cloneConnection={cloneConnection}
                    eventTimezone={eventTimezone}
                    handleOnChange={onChange}
                    showConfigurePassportForm={showConfigurePassportForm}
                    setDeletePopoverConnection={setDeletePopoverConnection}
                    onConnectionStatusChange={onConnectionStatusChange}
                    controlledCheckbox={controlledCheckbox}
                    checkbox={checkbox}
                    webinarReportsVM={webinarReportsVM}
                    handleTrackingEvent={handleTrackingEvent}
                    handleTroubleshoot={handleTroubleshoot}
                    trackHostJoinClick={trackHostJoinClick}
                    hideChevron={hideChevron}
                    meetingParams={meetingParams}
                    onOpenKnovioPreviewLinkPopover={() => openKnovioPreviewLinkPopover(childConnection)}
                    shouldDisableDeleteButton={shouldDisableDeleteButton(childConnection)}
                    connectionVersion={connectionVersion}
                    openFrom={openFrom}
                />
            </div>
        ));
    };

    return (
        <>
            {connections.map(connection => (<React.Fragment key={connection.meetingConnectionId}>
                <div className='connection-list__connection-wrapper'>
                    <ConnectionDetailsItem
                        key={connection.meetingConnectionId}
                        connection={connection}
                        cloneConnection={cloneConnection}
                        eventTimezone={eventTimezone}
                        isDefault={connection.meetingConnectionId === defaultConnectionId}
                        handleOnChange={onChange}
                        showConfigurePassportForm={showConfigurePassportForm}
                        setDeletePopoverConnection={setDeletePopoverConnection}
                        onConnectionStatusChange={onConnectionStatusChange}
                        controlledCheckbox={controlledCheckbox}
                        checkbox={checkbox}
                        webinarReportsVM={webinarReportsVM}
                        handleTrackingEvent={handleTrackingEvent}
                        handleTroubleshoot={handleTroubleshoot}
                        trackHostJoinClick={trackHostJoinClick}
                        hideChevron={hideChevron}
                        meetingParams={meetingParams}
                        onOpenKnovioPreviewLinkPopover={() => openKnovioPreviewLinkPopover(connection)}
                        shouldDisableDeleteButton={shouldDisableDeleteButton(connection)}
                        connectionVersion={connectionVersion}
                        openFrom={openFrom}
                    />
                </div>
                {connection.childConnections && connection.childConnections.length > 0 && (
                    <div>
                      {renderChildConnections(connection.childConnections)}
                    </div>
                )}
            </React.Fragment>))}
            <KnovioPreviewLinkPopover vm={knovioPreviewLinkPopoverInstance}/>
            <ConfirmDeletePopover
                handleOnDelete={onDelete}
                deletePopoverConnection={deletePopoverConnection}
                setDeletePopoverConnection={setDeletePopoverConnection}
            />
        </>
    );
};

export {
    ControlFormConnections,
    ControlFormListOfConnections,
    ControlFormConnectionsRow,
    ControlFormConnectionsNotes
};
